import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Text,
  Flex,
  Progress,
  VStack,
  Tooltip,
  Button,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

// MRR area chart with Y-axis $ labels and X-axis date labels — matches reference design
const MrrChart = ({ data }) => {
  if (!data || data.length < 2) return (
    <Box py={6} textAlign="center">
      <Text fontSize="xs" color="gray.300">No revenue data yet</Text>
    </Box>
  );

  const W = 500, H = 120, PAD_L = 48, PAD_B = 24, PAD_T = 8;
  const chartW = W - PAD_L;
  const chartH = H - PAD_B - PAD_T;

  const maxMrr = Math.max(...data.map(d => d.mrr), 1);
  // Round max up to a nice number for Y axis
  const yMax = Math.ceil(maxMrr / 100) * 100 || 100;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => yMax * t);

  const toX = (i) => PAD_L + (i / (data.length - 1)) * chartW;
  const toY = (v) => PAD_T + chartH - (v / yMax) * chartH;

  const pts = data.map((d, i) => [toX(i), toY(d.mrr)]);
  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${toX(data.length - 1)},${toY(0)} L${toX(0)},${toY(0)} Z`;

  // X-axis: show first and last date
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' });
  const fmtNzd = (v) => v >= 1000 ? `NZ$${(v / 1000).toFixed(0)}k` : `NZ$${v.toFixed(0)}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '140px' }}>
      <defs>
        <linearGradient id="mrr-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a1a2e" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#1a1a2e" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Y-axis gridlines + labels */}
      {yTicks.reverse().map((v, i) => {
        const y = toY(v);
        return (
          <g key={i}>
            <line x1={PAD_L} y1={y} x2={W} y2={y} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={PAD_L - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#a0aec0">{fmtNzd(v)}</text>
          </g>
        );
      })}
      {/* Area fill */}
      <path d={areaPath} fill="url(#mrr-grad)" />
      {/* Line */}
      <path d={linePath} fill="none" stroke="#1a1a2e" strokeWidth="1.5" />
      {/* X-axis labels */}
      <text x={toX(0)} y={H - 4} textAnchor="start" fontSize="9" fill="#a0aec0">{fmtDate(data[0].day)}</text>
      <text x={toX(data.length - 1)} y={H - 4} textAnchor="end" fontSize="9" fill="#a0aec0">{fmtDate(data[data.length - 1].day)}</text>
    </svg>
  );
};


const AdminHome = () => {
  const [stats, setStats] = useState(null);
  const [pilotData, setPilotData] = useState(null);
  const [bizSummary, setBizSummary] = useState(null);
  const [taskSummary, setTaskSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, pilotRes, bizRes, taskRes] = await Promise.all([
          fetchWithAuth('/api/admin/stats'),
          fetchWithAuth('/api/admin/pilot/success-dashboard'),
          fetchWithAuth('/api/admin/business-summary'),
          fetchWithAuth('/api/admin/agent-tasks/summary'),
        ]);
        if (statsRes.ok) setStats(await statsRes.json());
        if (pilotRes.ok) setPilotData(await pilotRes.json());
        if (bizRes.ok) setBizSummary(await bizRes.json());
        if (taskRes.ok) setTaskSummary(await taskRes.json());
      } catch (error) {
        console.error('Error fetching admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="lg" />
      </Box>
    );
  }

  const formatWeek = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const maxPlanTrend = Math.max(...(stats?.planTrend?.map(d => d.plans) || [1]), 1);
  const maxSignupTrend = Math.max(...(stats?.signupTrend?.map(d => d.signups) || [1]), 1);

  const workflowLabels = {
    'quick_plan': 'Quick Strategy',
    'autonomous_design_strategy': 'Full Strategy',
  };

  const totalWorkflows = Object.values(stats?.workflowBreakdown || {}).reduce((a, b) => a + b, 0) || 1;

  // Calculate value score using ADOPTION-BASED metrics (% of USERS getting value)
  const am = stats?.adoptionMetrics || {};
  const vm = stats?.validationMetrics || {};
  
  // User adoption rates (what % of users achieved this outcome)
  const strategyAdoption = am.strategyAdoptionRate || 0;  // % users with strategies
  const docAdoption = am.docAdoptionRate || 0;            // % users with docs generated
  const shipAdoption = am.shipAdoptionRate || 0;          // % users who shipped via MCP
  const retentionRate = vm.returnUserRate || 0;           // % users returning after 7 days
  const planAdoption = am.planAdoptionRate || 0;          // % users who created a plan
  
  // Weighted score: Adoption breadth measures true product value
  // Outcomes (Strategy + Docs + Ship) 60% + Retention 25% + Initial Activation 15%
  const valueScore = Math.round(
    (strategyAdoption * 0.25) + (docAdoption * 0.20) + (shipAdoption * 0.15) +
    (retentionRate * 0.25) + (planAdoption * 0.15)
  );
  
  const getScoreColor = (score) => {
    if (score >= 60) return "green";
    if (score >= 30) return "orange";
    return "red";
  };


  return (
    <Box>
      <Heading size="lg" mb={6}>Dashboard</Heading>


      {/* Value Summary Card */}
      <Card variant="outline" mb={6} bg="gray.50" borderColor="gray.200">
        <CardBody>
          <Flex direction={{ base: "column", lg: "row" }} gap={6} align="center">
            <Box textAlign="center" minW="140px">
              <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>PABS VALUE SCORE</Text>
              <Text fontSize="5xl" fontWeight="bold" color={`${getScoreColor(valueScore)}.500`}>
                {valueScore}
              </Text>
              <Text fontSize="xs" color="gray.400">Composite metric</Text>
            </Box>
            
            <Box flex="1">
              <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
                {[
                  { value: strategyAdoption, label: 'Strategy Adoption', sub: 'Users with strategies', tooltip: `${am.usersWithStrategies || 0} of ${am.totalNonAdminUsers || 0} users`, green: 60, orange: 30 },
                  { value: docAdoption, label: 'Doc Adoption', sub: 'Users with PRDs', tooltip: `${am.usersWithDocs || 0} of ${am.totalNonAdminUsers || 0} users`, green: 30, orange: 15 },
                  { value: shipAdoption, label: 'Ship Adoption', sub: 'Users who shipped', tooltip: `${am.usersWithMcp || 0} of ${am.totalNonAdminUsers || 0} users`, green: 20, orange: 10 },
                  { value: retentionRate, label: '7-Day Retention', sub: 'Users returning', tooltip: `${vm.returningUsers || 0} of ${vm.totalUsersWithPlans || 0} users`, green: 40, orange: 20 },
                  { value: planAdoption, label: 'Plan Adoption', sub: 'Users with plans', tooltip: `${am.usersWithPlans || 0} of ${am.totalNonAdminUsers || 0} users`, green: 60, orange: 30 },
                ].map(({ value, label, sub, tooltip, green, orange }) => {
                  const color = value >= green ? 'green' : value >= orange ? 'orange' : 'red';
                  return (
                    <Tooltip key={label} label={tooltip}>
                      <Box textAlign="center" p={3} bg={`${color}.50`} borderRadius="md" shadow="sm" cursor="help">
                        <Text fontSize="2xl" fontWeight="bold" color={`${color}.600`}>{value}%</Text>
                        <Text fontSize="xs" color="gray.600">{label}</Text>
                        <Text fontSize="xs" color="gray.400">{sub}</Text>
                      </Box>
                    </Tooltip>
                  );
                })}
              </SimpleGrid>
              <Text fontSize="xs" color="gray.400" mt={3} textAlign="center">
                Value = Outcomes (Strategy 25% + Docs 20% + Ship 15%) + Retention 25% + Activation 15% | Admin users excluded
              </Text>
            </Box>
          </Flex>
        </CardBody>
      </Card>

      <Card variant="outline" mb={6}>
        <CardHeader pb={2}>
          <Heading size="sm">Quantitative Metrics</Heading>
        </CardHeader>
        <CardBody pt={2}>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4} mb={4}>
            {bizSummary && (
              <Box textAlign="center" p={3} bg="green.50" borderRadius="md">
                <Text fontSize="2xl" fontWeight="bold" color="green.700">
                  NZ${(bizSummary.arr || (bizSummary.mrr * 12) || 0).toLocaleString('en-NZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Text>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">ARR</Text>
                <Text fontSize="xs" color="gray.400">Annual run rate</Text>
              </Box>
            )}
            {bizSummary && (
              <Box textAlign="center" p={3} bg="green.50" borderRadius="md">
                <Text fontSize="2xl" fontWeight="bold" color="green.700">
                  NZ${(bizSummary.mrr || 0).toFixed(2)}
                </Text>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">MRR</Text>
                <Text fontSize="xs" color="gray.400">
                  {bizSummary.mrrWow !== null && bizSummary.mrrWow !== undefined
                    ? `${bizSummary.mrrWow >= 0 ? '+' : ''}${bizSummary.mrrWow}% MoM`
                    : 'Monthly recurring'}
                </Text>
              </Box>
            )}
            <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats?.totalUsers || 0}</Text>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">Total Users</Text>
              {stats?.recentSignups > 0 && (
                <Text fontSize="xs" color="green.500">+{stats.recentSignups} this week</Text>
              )}
            </Box>
            <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats?.weeklyActiveUsers || 0}</Text>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">Weekly Active</Text>
              <Text fontSize="xs" color="gray.400">Last 7 days</Text>
            </Box>
            <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats?.monthlyActiveUsers || 0}</Text>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">Monthly Active</Text>
              <Text fontSize="xs" color="gray.400">Last 30 days</Text>
            </Box>
            <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats?.totalPlans || 0}</Text>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">Total Plans</Text>
              <Text fontSize="xs" color="gray.400">Created plans</Text>
            </Box>
            <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats?.totalStrategies || 0}</Text>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">Strategies</Text>
              <Text fontSize="xs" color="gray.400">Product strategies</Text>
            </Box>
            <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats?.totalCompetitorAnalyses || 0}</Text>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">Competitor Analyses</Text>
              <Text fontSize="xs" color="gray.400">Market research</Text>
            </Box>
            <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats?.plansWithPrograms || 0}</Text>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">Action Plans</Text>
              <Text fontSize="xs" color="gray.400">With programs</Text>
            </Box>
            <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats?.totalConversations || 0}</Text>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">Chat Conversations</Text>
              <Text fontSize="xs" color="gray.400">AI interactions</Text>
            </Box>
            <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="gray.700">{stats?.activeCodes || 0}</Text>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">Active Passcodes</Text>
              <Text fontSize="xs" color="gray.400">Available invites</Text>
            </Box>
          </SimpleGrid>
          <Box pt={2} borderTop="1px solid" borderColor="gray.100">
            <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={3}>Per-user averages <Text as="span" color="gray.400" fontWeight="normal">(all {am.totalNonAdminUsers || 0} users)</Text></Text>
            <SimpleGrid columns={{ base: 3 }} spacing={4}>
              {[
                { label: 'Briefs / user', value: am.totalNonAdminUsers > 0 ? (stats.totalPlans / am.totalNonAdminUsers).toFixed(1) : '—' },
                { label: 'Strategies / user', value: am.totalNonAdminUsers > 0 ? (stats.totalStrategies / am.totalNonAdminUsers).toFixed(1) : '—' },
                { label: 'Chats / user', value: am.totalNonAdminUsers > 0 ? (stats.totalConversations / am.totalNonAdminUsers).toFixed(1) : '—' },
              ].map(({ label, value }) => (
                <Box key={label} textAlign="center" p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">{value}</Text>
                  <Text fontSize="xs" color="gray.600" fontWeight="medium">{label}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
          {bizSummary?.mrrTrend?.length > 1 && (
            <Box pt={4} borderTop="1px solid" borderColor="gray.100">
              <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={2}>MRR Trend</Text>
              <MrrChart data={bizSummary.mrrTrend} />
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Agent Tasks Summary */}
      {taskSummary && (
        <Card variant="outline" mb={6}>
          <CardHeader pb={2}>
            <Heading size="sm">Agent Tasks</Heading>
          </CardHeader>
          <CardBody pt={2}>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box textAlign="center" p={3} bg="purple.50" borderRadius="md">
                <Text fontSize="2xl" fontWeight="bold" color="purple.600">{taskSummary.total}</Text>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">Total Tasks</Text>
                <Text fontSize="xs" color="gray.400">All time</Text>
              </Box>
              <Box textAlign="center" p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">{taskSummary.this_week}</Text>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">This Week</Text>
                <Text fontSize="xs" color="gray.400">Last 7 days</Text>
              </Box>
              <Box textAlign="center" p={3} bg={taskSummary.completion_rate >= 70 ? 'green.50' : taskSummary.completion_rate >= 40 ? 'orange.50' : 'red.50'} borderRadius="md">
                <Text fontSize="2xl" fontWeight="bold" color={taskSummary.completion_rate >= 70 ? 'green.600' : taskSummary.completion_rate >= 40 ? 'orange.600' : 'red.600'}>{taskSummary.completion_rate}%</Text>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">Completion Rate</Text>
                <Text fontSize="xs" color="gray.400">{taskSummary.completed} completed</Text>
              </Box>
              <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="bold" color="gray.700" noOfLines={1}>
                  {taskSummary.top_capabilities[0]?.capability?.replace(/_/g, ' ') || '—'}
                </Text>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">Top Capability</Text>
                <Text fontSize="xs" color="gray.400">{taskSummary.top_capabilities[0]?.count || 0} tasks</Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        <Card variant="outline">
          <CardHeader pb={2}>
            <Heading size="sm">Plans Created (Last 12 Weeks)</Heading>
          </CardHeader>
          <CardBody pt={2}>
            {stats?.planTrend?.length > 0 ? (
              <VStack spacing={2} align="stretch">
                {stats.planTrend.map((week, idx) => (
                  <Flex key={idx} align="center" gap={3}>
                    <Text fontSize="xs" color="gray.500" w="60px">
                      {formatWeek(week.week)}
                    </Text>
                    <Box flex={1}>
                      <Progress
                        value={(week.plans / maxPlanTrend) * 100}
                        size="sm"
                        colorScheme="blue"
                        borderRadius="full"
                      />
                    </Box>
                    <Text fontSize="sm" fontWeight="600" w="30px" textAlign="right">
                      {week.plans}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            ) : (
              <Text color="gray.400" fontSize="sm">No data available</Text>
            )}
          </CardBody>
        </Card>

        <Card variant="outline">
          <CardHeader pb={2}>
            <Heading size="sm">User Signups (Last 12 Weeks)</Heading>
          </CardHeader>
          <CardBody pt={2}>
            {stats?.signupTrend?.length > 0 ? (
              <VStack spacing={2} align="stretch">
                {stats.signupTrend.map((week, idx) => (
                  <Flex key={idx} align="center" gap={3}>
                    <Text fontSize="xs" color="gray.500" w="60px">
                      {formatWeek(week.week)}
                    </Text>
                    <Box flex={1}>
                      <Progress
                        value={(week.signups / maxSignupTrend) * 100}
                        size="sm"
                        colorScheme="blue"
                        borderRadius="full"
                      />
                    </Box>
                    <Text fontSize="sm" fontWeight="600" w="30px" textAlign="right">
                      {week.signups}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            ) : (
              <Text color="gray.400" fontSize="sm">No data available</Text>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card variant="outline" mb={6}>
        <CardHeader pb={2}>
          <Heading size="sm">Workflow Breakdown</Heading>
        </CardHeader>
        <CardBody pt={2}>
          <VStack spacing={3} align="stretch">
            {Object.entries(stats?.workflowBreakdown || {}).map(([workflow, count]) => (
              <Box key={workflow}>
                <Flex justify="space-between" mb={1}>
                  <Text fontSize="sm">{workflowLabels[workflow] || workflow}</Text>
                  <Text fontSize="sm" fontWeight="600">{count}</Text>
                </Flex>
                <Progress
                  value={(count / totalWorkflows) * 100}
                  size="sm"
                  colorScheme="blue"
                  borderRadius="full"
                />
              </Box>
            ))}
            {Object.keys(stats?.workflowBreakdown || {}).length === 0 && (
              <Text color="gray.400" fontSize="sm">No workflow data</Text>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Pilot Program Snapshot */}
      <Card variant="outline" mb={6} borderColor="purple.200" bg="purple.50">
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size="sm" color="purple.700">Pilot Program</Heading>
              <Text fontSize="xs" color="purple.500">Commercial hypothesis validation — which segment is our customer?</Text>
            </Box>
            <Button size="sm" colorScheme="purple" variant="outline" onClick={() => navigate('/admin/pilot')}>
              View Pilot Tracker →
            </Button>
          </Flex>
        </CardHeader>
        <CardBody pt={2}>
          {pilotData ? (() => {
            const cr = pilotData.conversion_readiness || [];
            const readyCount = cr.filter(r => r.readiness === 'ready').length;
            const warmCount = cr.filter(r => r.readiness === 'warm').length;
            const coldCount = cr.filter(r => r.readiness === 'cold').length;
            return (
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Box textAlign="center" p={3} bg="white" borderRadius="md" shadow="sm">
                  <Text fontSize="2xl" fontWeight="bold" color="purple.600">{pilotData.total_participants || 0}</Text>
                  <Text fontSize="xs" color="gray.600">Participants</Text>
                </Box>
                <Box textAlign="center" p={3} bg="green.50" borderRadius="md" shadow="sm">
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">{readyCount}</Text>
                  <Text fontSize="xs" color="gray.600">Ready to Convert</Text>
                </Box>
                <Box textAlign="center" p={3} bg="yellow.50" borderRadius="md" shadow="sm">
                  <Text fontSize="2xl" fontWeight="bold" color="yellow.600">{warmCount}</Text>
                  <Text fontSize="xs" color="gray.600">Warm</Text>
                </Box>
                <Box textAlign="center" p={3} bg="gray.100" borderRadius="md" shadow="sm">
                  <Text fontSize="2xl" fontWeight="bold" color="gray.500">{coldCount}</Text>
                  <Text fontSize="xs" color="gray.600">Cold</Text>
                </Box>
              </SimpleGrid>
            );
          })() : (
            <Text fontSize="sm" color="purple.400">No pilot data yet</Text>
          )}
        </CardBody>
      </Card>

    </Box>
  );
};

export default AdminHome;
